import { BudgetRecipientEnum } from '../components/budgetsForm/createAndEdit/types';
import { OccurrenceDto, SummaryOccurrenceDto } from '../dtos/occurrenceDto';
import { OccurrenceStepEnum } from '../enums/occurrenceStep';
import {
  Occurrence,
  OccurrenceWithTimeSpent,
  QuantityOccurrenceAnalytics,
} from '../models/occurrence';
import ApiService from '../services/api';
import { getStepInfo } from '../utils/kanbanUtils';

enum EUserRole {
  TOWER = 'TOWER',
  CONSULTANT = 'CONSULTANT',
  MANAGER = 'MANAGER',
  PARTNERSHIP = 'PARTNERSHIP',
}

let cancelAbortControllerList: AbortController | undefined;
let cancelAbortControllerListByScheduleIds: AbortController | undefined;
let cancelAbortControllerListWithTimeSpent: AbortController | undefined;
let cancelAbortController: AbortController | undefined;
let cancelAbortControllerAnalytics: AbortController | undefined;
let cancelAbortControllerStepAvailable: AbortController | undefined;
let cancelAbortControllerListWithTimeSpentForIndicators: AbortController | undefined;

export const fetchOccurenceList = async (
    filters: {
      criticalityFilter?: string | null,
      typeFilter?: string[] | null,
      isAlertFilter?: string | null,
      regionalFilter?: string[] | null,
      cellFilter?: string[] | null,
      chassisFilter?: string | null,
      chassisListFilter?: string[] | null,
      chassisListFilterByStep?: string[] | null,
      startDateFilter?: string | null,
      endDateFilter?: string | null,
      updateDateFilter?: string | null,
      updateDateFilterEnd?: string | null,
      dealershipFilter?: string[] | null,
      modelFilter?: string[] | null,
      stepFilter?: OccurrenceStepEnum | null,
      stepTypeCurrentFilter?: OccurrenceStepEnum | null,
      searchFieldName?: string | null,
      searchFieldValue?: string | null,
      cpvNameFilter?: string | null,
      sortBySteps?: string | null,
      sortOrder?: string | null,
    },
    userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP',
    page: number,
    limit: number = 20,
    finalizedFilter?: boolean | null,
): Promise<{ occurrences: Occurrence[], total: number, totalInProgress: number, totalPages: number, totalInMinutes: number }> => {
    if(cancelAbortControllerList) cancelAbortControllerList.abort();
    cancelAbortControllerList = new AbortController();

  const apiService = new ApiService();
  try {
    const createdAtFilter =
      filters.startDateFilter && filters.endDateFilter
        ? `&createdStartAt=${filters.startDateFilter}&createdEndAt=${filters.endDateFilter}`
        : '';
    const updatedAtFilter =
      filters.updateDateFilter && filters.updateDateFilterEnd
        ? `&updatedStartAt=${filters.updateDateFilter}&updatedEndAt=${filters.updateDateFilterEnd}`
        : '';

    let typeFilter =
      filters.typeFilter && filters.typeFilter?.length > 0
        ? `&occurrenceType=${filters.typeFilter
            .filter((type) => type !== 'HAS_CAMPAIGNS')
            .join('&occurrenceType=')}`
        : '';
    if (filters.typeFilter && filters.typeFilter?.includes('HAS_CAMPAIGNS'))
      typeFilter += `&hasCampaigns=true`;
    if (
      filters.typeFilter &&
      (filters.typeFilter?.includes('PREVENTIVE') || filters.typeFilter?.includes('CORRECTIVE'))
    )
      typeFilter += `&occurrenceType=PREVENTIVE_AND_CORRECTIVE`;

    const dealershipParams =
      filters.dealershipFilter && filters.dealershipFilter.length > 0
        ? filters.dealershipFilter.map((dn) => `&dealershipDn=${dn}`).join('')
        : '';

    const regionalParams =
      filters.regionalFilter && filters.regionalFilter.length > 0
        ? filters.regionalFilter.map((r) => `&regional=${r}`).join('')
        : '';

    const cellParams =
      filters.cellFilter && filters.cellFilter.length > 0
        ? filters.cellFilter.map((c) => `&cell=${c}`).join('')
        : '';

        const searchParams = filters.searchFieldName && filters.searchFieldValue
            ? `&searchFieldName=${filters.searchFieldName}&searchFieldValue=${encodeURIComponent(filters.searchFieldValue)}`
            : '';

    const cpvParam = filters.cpvNameFilter
      ? `&cpvName=${encodeURIComponent(filters.cpvNameFilter)}`
      : '';

    const url = `/occurrence/list/${userRole}${finalizedFilter === true ? '/finished' : finalizedFilter === false ? '' : '/all'}?page=${page}&size=${limit}${filters.criticalityFilter ? `&criticality=${filters.criticalityFilter}` : ''}${typeFilter}${filters.chassisFilter ? `&chassis=${filters.chassisFilter}` : ''}${createdAtFilter}${updatedAtFilter}${dealershipParams}${regionalParams}${cellParams}${filters.stepFilter ? `&stepType=${filters.stepFilter}` : ''}${filters.stepTypeCurrentFilter ? `&stepTypeCurrent=${filters.stepTypeCurrentFilter}` : ''}${filters.isAlertFilter ? `&isAlert=${filters.isAlertFilter}` : ''}${searchParams}${cpvParam}&sortByStep=${filters.sortBySteps}&sortOrder=${filters.sortOrder}`;
    const response = await apiService.post<any>(url, {
      chassisList: (filters.chassisListFilterByStep?.length ?? 0) > 0 ? filters.chassisListFilterByStep : filters.chassisListFilter,
      models: filters.modelFilter && filters.modelFilter.length > 0 ? filters.modelFilter : undefined,
    }, { signal: cancelAbortControllerList.signal });
        if (response.status === 200) {
            cancelAbortControllerList = undefined;
            return {
                occurrences: response.data.occurrences.map(OccurrenceDto),
                total: response.data.totalElements,
                totalInProgress: response.data.totalWithSteps,
                totalPages: response.data.totalPages,
                totalInMinutes: response.data.totalMinutes,
            };
        } else {
            cancelAbortControllerList = undefined;
            return { occurrences: [], total: 0, totalInProgress: 0, totalPages: 0, totalInMinutes: 0 };
        }
    } catch (error: any) {
        if(error.code === "ERR_CANCELED") throw new Error("ERR_CANCELED");
        cancelAbortControllerList = undefined;
        console.error(error);
        return { occurrences: [], total: 0, totalInProgress: 0, totalPages: 0, totalInMinutes: 0 };
    }
};

export const fetchOccurrenceListByScheduleIds = async (
  userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP',
  scheduleIds: string[],
): Promise<Occurrence[]> => {
  if (cancelAbortControllerListByScheduleIds) cancelAbortControllerListByScheduleIds.abort();
  cancelAbortControllerListByScheduleIds = new AbortController();

  const apiService = new ApiService();
  try {
    const response = await apiService.post<any>(
      `/occurrence/list/${userRole}/by-schedule-ids`,
      { scheduleIds },
      { signal: cancelAbortControllerListByScheduleIds.signal },
    );
    if (response.status === 200) {
      cancelAbortControllerListByScheduleIds = undefined;
      return response.data.map(SummaryOccurrenceDto);
    } else {
      cancelAbortControllerListByScheduleIds = undefined;
      return [];
    }
  } catch (error: any) {
    if (error.code === 'ERR_CANCELED') throw new Error('ERR_CANCELED');
    cancelAbortControllerListByScheduleIds = undefined;
    console.error(error);
    return [];
  }
};

export const fetchOccurenceListWithTimeSpent = async (
    filters: {
        criticalityFilter?: string | null,
        typeFilter?: string[] | null,
        isAlertFilter?: string | null,
        chassisFilter?: string | null,
        chassisListFilter?: string[] | null,
        startDateFilter?: string | null,
        endDateFilter?: string | null,
        updateDateFilter?: string | null,
        updateDateFilterEnd?: string | null,
        dealershipFilter?: string[] | null,
        modelFilter?: string[] | null,
        stepTypeCurrentFilter?: OccurrenceStepEnum | null,
        searchFieldName?: string | null,
        searchFieldValue?: string | null,
    cpvNameFilter?: string | null,
    },
    userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP',
    finalizedFilter?: boolean,
): Promise<{current: OccurrenceWithTimeSpent[], finalized: OccurrenceWithTimeSpent[]}> => {
    if(cancelAbortControllerListWithTimeSpent) cancelAbortControllerListWithTimeSpent.abort();
    cancelAbortControllerListWithTimeSpent = new AbortController();

  const apiService = new ApiService();
  try {
    const createdAtFilter =
      filters.startDateFilter && filters.endDateFilter
        ? `&createdStartAt=${filters.startDateFilter}&createdEndAt=${filters.endDateFilter}`
        : '';
    const updatedAtFilter =
      filters.updateDateFilter && filters.updateDateFilterEnd
        ? `&updatedStartAt=${filters.updateDateFilter}&updatedEndAt=${filters.updateDateFilterEnd}`
        : '';

    let typeFilter =
      filters.typeFilter && filters.typeFilter?.length > 0
        ? `&occurrenceType=${filters.typeFilter
            .filter((type) => type !== 'HAS_CAMPAIGNS')
            .join('&occurrenceType=')}`
        : '';
    if (filters.typeFilter && filters.typeFilter?.includes('HAS_CAMPAIGNS'))
      typeFilter += `&hasCampaigns=true`;
    if (
      filters.typeFilter &&
      (filters.typeFilter?.includes('PREVENTIVE') || filters.typeFilter?.includes('CORRECTIVE'))
    )
      typeFilter += `&occurrenceType=PREVENTIVE_AND_CORRECTIVE`;

    const dealershipParams =
      filters.dealershipFilter && filters.dealershipFilter.length > 0
        ? filters.dealershipFilter.map((dn) => `&dealershipDn=${dn}`).join('')
        : '';

        const regionalParams = (filters as any).regionalFilter && (filters as any).regionalFilter.length > 0
            ? (filters as any).regionalFilter.map((r: string) => `&regional=${r}`).join('')
            : '';
        const cellParams = (filters as any).cellFilter && (filters as any).cellFilter.length > 0
            ? (filters as any).cellFilter.map((c: string) => `&cell=${c}`).join('')
            : '';

        const searchParams = filters.searchFieldName && filters.searchFieldValue
            ? `&searchFieldName=${filters.searchFieldName}&searchFieldValue=${encodeURIComponent(filters.searchFieldValue)}`
            : '';

    const cpvParam = filters.cpvNameFilter
      ? `&cpvName=${encodeURIComponent(filters.cpvNameFilter)}`
      : '';

    const timeSpentSuffix = finalizedFilter === true ? '/finished' : finalizedFilter === false ? '' : '/all';
    const url = `/occurrence/list/${userRole}/time-spent${timeSpentSuffix}?${filters.criticalityFilter ? `&criticality=${filters.criticalityFilter}` : ''}${typeFilter}${filters.chassisFilter ? `&chassis=${filters.chassisFilter}` : ''}${createdAtFilter}${updatedAtFilter}${dealershipParams}${regionalParams}${cellParams}${filters.isAlertFilter ? `&isAlert=${filters.isAlertFilter}` : ''}${filters.stepTypeCurrentFilter ? `&stepTypeCurrent=${filters.stepTypeCurrentFilter}` : ''}${searchParams}${cpvParam}`;
    const response = await apiService.post<any>(url, {
      chassisList: filters.chassisListFilter,
      models: filters.modelFilter && filters.modelFilter.length > 0 ? filters.modelFilter : undefined,
    }, { signal: cancelAbortControllerListWithTimeSpent.signal });
        if (response.status === 200) {
            const data: OccurrenceWithTimeSpent[] = response.data
            .map((item: any) => {
                const currentStep = item.steps.find((step: any) => !step.hasFinished);
                const currentStepTotalTime = item.steps.filter((step: any) => currentStep && step.stepId === currentStep.stepId).reduce((acc: number, step: any) => acc + step.totalMinutes, 0);

                return {
                    occurrenceId: item.occurrenceUuid,
                    timeSpent: item.totalSeconds,
                  occurrenceType: item.occurrenceType,
                    totalMinutesCard: item.totalMinutesCard,
                    totalSecondsCard: item.totalSecondsCard,
                    customer: (item.customer || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    model: (item.modelName || item.model || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                  legislation: (item.emissionStandard || item.legislation || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    city: (item.city || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    state: (item.state || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    dealershipName: (item.dealershipName || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    dn: (item.dn || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                  chassis: (item.chassis || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                  cpvName: item.cpvName || item.cpv?.name || undefined,
                    steps: getStepInfo(item.steps.map((step: any) => ({
                        stepId: step.stepId,
                        timeSpent: step.totalSeconds,
                        stepStart: new Date(step.stepStart),
                        hasFinished: step.hasFinished,
                    }))),
                    currentStep: currentStep?.stepId,
                    currentStepTotalTime: currentStepTotalTime,
                    currentStepStart: currentStep && new Date(currentStep.stepStart),
                };
            });

      cancelAbortControllerListWithTimeSpent = undefined;
      return {
        current: data.filter((item: OccurrenceWithTimeSpent) => item.currentStep),
        finalized: data.filter((item: OccurrenceWithTimeSpent) => !item.currentStep),
      };
    } else {
      cancelAbortControllerListWithTimeSpent = undefined;
      return { current: [], finalized: [] };
    }
  } catch (error: any) {
    if (error.code === 'ERR_CANCELED') throw new Error('ERR_CANCELED');
    cancelAbortControllerListWithTimeSpent = undefined;
    console.error(error);
    return { current: [], finalized: [] };
  }
};

export const fetchOccurenceListWithTimeSpentForIndicators = async (
    filters: {
        criticalityFilter?: string | null,
        typeFilter?: string[] | null,
        isAlertFilter?: string | null,
        chassisFilter?: string | null,
        chassisListFilter?: string[] | null,
        startDateFilter?: string | null,
        endDateFilter?: string | null,
        updateDateFilter?: string | null,
        updateDateFilterEnd?: string | null,
        dealershipFilter?: string[] | null,
        modelFilter?: string[] | null,
        searchFieldName?: string | null,
        searchFieldValue?: string | null,
    },
    userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP',
    finalizedFilter?: boolean,
): Promise<{current: OccurrenceWithTimeSpent[], finalized: OccurrenceWithTimeSpent[]}> => {
    if(cancelAbortControllerListWithTimeSpentForIndicators) cancelAbortControllerListWithTimeSpentForIndicators.abort();
    cancelAbortControllerListWithTimeSpentForIndicators = new AbortController();

  const apiService = new ApiService();
  try {
    const createdAtFilter =
      filters.startDateFilter && filters.endDateFilter
        ? `&createdStartAt=${filters.startDateFilter}&createdEndAt=${filters.endDateFilter}`
        : '';
    const updatedAtFilter =
      filters.updateDateFilter && filters.updateDateFilterEnd
        ? `&updatedStartAt=${filters.updateDateFilter}&updatedEndAt=${filters.updateDateFilterEnd}`
        : '';

    let typeFilter =
      filters.typeFilter && filters.typeFilter?.length > 0
        ? `&occurrenceType=${filters.typeFilter
            .filter((type) => type !== 'HAS_CAMPAIGNS')
            .join('&occurrenceType=')}`
        : '';
    if (filters.typeFilter && filters.typeFilter?.includes('HAS_CAMPAIGNS'))
      typeFilter += `&hasCampaigns=true`;
    if (
      filters.typeFilter &&
      (filters.typeFilter?.includes('PREVENTIVE') || filters.typeFilter?.includes('CORRECTIVE'))
    )
      typeFilter += `&occurrenceType=PREVENTIVE_AND_CORRECTIVE`;

    const dealershipParams =
      filters.dealershipFilter && filters.dealershipFilter.length > 0
        ? filters.dealershipFilter.map((dn) => `&dealershipDn=${dn}`).join('')
        : '';

        const regionalParams = (filters as any).regionalFilter && (filters as any).regionalFilter.length > 0
            ? (filters as any).regionalFilter.map((r: string) => `&regional=${r}`).join('')
            : '';
        const cellParams = (filters as any).cellFilter && (filters as any).cellFilter.length > 0
            ? (filters as any).cellFilter.map((c: string) => `&cell=${c}`).join('')
            : '';

        const searchParams = filters.searchFieldName && filters.searchFieldValue
            ? `&searchFieldName=${filters.searchFieldName}&searchFieldValue=${encodeURIComponent(filters.searchFieldValue)}`
            : '';

    const url = `/occurrence/list/${userRole}/time-spent/all?${filters.criticalityFilter ? `&criticality=${filters.criticalityFilter}` : ''}${typeFilter}${filters.chassisFilter ? `&chassis=${filters.chassisFilter}` : ''}${createdAtFilter}${updatedAtFilter}${dealershipParams}${regionalParams}${cellParams}${filters.isAlertFilter ? `&isAlert=${filters.isAlertFilter}` : ''}${searchParams}`;
    const response = await apiService.post<any>(url, {
      chassisList: filters.chassisListFilter,
      models: filters.modelFilter && filters.modelFilter.length > 0 ? filters.modelFilter : undefined,
    }, { signal: cancelAbortControllerListWithTimeSpentForIndicators.signal });
        if (response.status === 200) {
            const data: OccurrenceWithTimeSpent[] = response.data
            .map((item: any) => {
                const currentStep = item.steps.find((step: any) => !step.hasFinished);
                const currentStepTotalTime = item.steps.filter((step: any) => currentStep && step.stepId === currentStep.stepId).reduce((acc: number, step: any) => acc + step.totalMinutes, 0);

                return {
                    occurrenceId: item.occurrenceUuid,
                    timeSpent: item.totalSeconds,
                  occurrenceType: item.occurrenceType,
                    totalMinutesCard: item.totalMinutesCard,
                    totalSecondsCard: item.totalSecondsCard,
                    customer: (item.customer || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                  model: (item.modelName || item.model || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                  legislation: (item.emissionStandard || item.legislation || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    city: (item.city || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    state: (item.state || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    dealershipName: (item.dealershipName || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    dn: (item.dn || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    chassis: (item.chassis || 'Não identificado').trim().replace(/\s{2,}/g, ' '),
                    steps: getStepInfo(item.steps.map((step: any) => ({
                        stepId: step.stepId,
                        timeSpent: step.totalSeconds,
                        stepStart: new Date(step.stepStart),
                        hasFinished: step.hasFinished,
                    }))),
                    currentStep: currentStep?.stepId,
                    currentStepTotalTime: currentStepTotalTime,
                    currentStepStart: currentStep && new Date(currentStep.stepStart),
                };
            });

      cancelAbortControllerListWithTimeSpentForIndicators = undefined;
      return {
        current: data.filter((item: OccurrenceWithTimeSpent) => item.currentStep),
        finalized: data.filter((item: OccurrenceWithTimeSpent) => !item.currentStep),
      };
    } else {
      cancelAbortControllerListWithTimeSpentForIndicators = undefined;
      return { current: [], finalized: [] };
    }
  } catch (error: any) {
    if (error.code === 'ERR_CANCELED') throw new Error('ERR_CANCELED');
    cancelAbortControllerListWithTimeSpentForIndicators = undefined;
    console.error(error);
    return { current: [], finalized: [] };
  }
};

export const fetchOccurrence = async (
  occurrenceId: string,
  userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP',
  finalizedFilter?: boolean | null,
): Promise<Occurrence | undefined> => {
  if (cancelAbortController) cancelAbortController.abort();
  cancelAbortController = new AbortController();

  const apiService = new ApiService();
  try {
    const response = await apiService.get(`/occurrence/${userRole}/${occurrenceId}`, {
      signal: cancelAbortController.signal,
    });
    if (response.status === 200) {
      cancelAbortController = undefined;
      return OccurrenceDto(response.data);
    } else {
      cancelAbortController = undefined;
      return undefined;
    }
  } catch (error: any) {
    if (error.code === 'ERR_CANCELED') throw new Error('ERR_CANCELED');
    cancelAbortController = undefined;
    console.error(error);
    return undefined;
  }
};

export const fetchExternalOccurrence = async (
  protocolNumber: string,
): Promise<Occurrence | undefined> => {
  const apiService = new ApiService();
  const response = await apiService.get(`/occurrence/external/${protocolNumber}`);
  if (response.status === 200) return OccurrenceDto(response.data);
  return undefined;
};

export interface IOccurrenceVehicleData {
  assetId?: string;
  model?: string;
  year?: number;
  licensePlate?: string;
  chassis?: string;
  odometer?: number;
  hourMeter?: number | string | null;
  hourmeter?: number | string | null;
  inWarranty?: boolean;
  vehicleName?: string;
  vehicleType?: string;
  emissionStandard?: string;
  protocolNumber?: string;
  contractNumber?: string | null;
  name?: string;
  email?: string;
  phone?: string;
}

export const fetchOccurrenceVehicle = async (
  occurrenceId: string,
): Promise<IOccurrenceVehicleData | undefined> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.get(`/occurrence/${occurrenceId}/vehicle`);
    if (response.status === 200 && response.data) {
      return response.data as IOccurrenceVehicleData;
    }
    return undefined;
  } catch (error) {
    console.error('Erro ao buscar dados do veículo:', error);
    return undefined;
  }
};

export interface IOccurrenceCustomerData {
  name?: string;
  phone?: string;
  email?: string;
}

export const fetchOccurrenceCustomer = async (
  occurrenceId: string,
): Promise<IOccurrenceCustomerData | undefined> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.get(`/occurrence/${occurrenceId}/customer`);
    if (response.status === 200 && response.data) {
      return response.data as IOccurrenceCustomerData;
    }
    return undefined;
  } catch (error) {
    console.error('Erro ao buscar dados do cliente:', error);
    return undefined;
  }
};

export const fetchQuantityOccurrenceChartAnalytics = async (
    filters: {
        criticalityFilter?: string | null,
        chassisFilter?: string | null,
        chassisListFilter?: string[] | null,
        startDateFilter?: string | null,
        endDateFilter?: string | null,
        updateDateFilter?: string | null,
        updateDateFilterEnd?: string | null,
        typeFilter?: string[] | null,
        dealershipFilter?: string[] | null,
        modelFilter?: string[] | null,
        stepFilter?: OccurrenceStepEnum | null,
        searchFieldName?: string | null,
        searchFieldValue?: string | null,
    },
    userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP',
    finalizedFilter?: boolean | null,
): Promise<QuantityOccurrenceAnalytics> => {
  if (cancelAbortControllerAnalytics) cancelAbortControllerAnalytics.abort();
  cancelAbortControllerAnalytics = new AbortController();

  const apiService = new ApiService();
  try {
    const createdAtFilter =
      filters.startDateFilter && filters.endDateFilter
        ? `&createdStartAt=${filters.startDateFilter}&createdEndAt=${filters.endDateFilter}`
        : '';
    const updatedAtFilter =
      filters.updateDateFilter && filters.updateDateFilterEnd
        ? `&updatedStartAt=${filters.updateDateFilter}&updatedEndAt=${filters.updateDateFilterEnd}`
        : '';

    let typeFilter =
      filters.typeFilter && filters.typeFilter?.length > 0
        ? `&occurrenceType=${filters.typeFilter
            .filter((type) => type !== 'HAS_CAMPAIGNS')
            .join('&occurrenceType=')}`
        : '';
    if (filters.typeFilter && filters.typeFilter?.includes('HAS_CAMPAIGNS'))
      typeFilter += `&hasCampaigns=true`;
    if (
      filters.typeFilter &&
      (filters.typeFilter?.includes('PREVENTIVE') || filters.typeFilter?.includes('CORRECTIVE'))
    )
      typeFilter += `&occurrenceType=PREVENTIVE_AND_CORRECTIVE`;

      const dealershipParams = filters.dealershipFilter && filters.dealershipFilter.length > 0 
          ? filters.dealershipFilter.map(dn => `&dealershipDn=${dn}`).join('') 
          : '';
      
      const regionalParamsA = (filters as any).regionalFilter && (filters as any).regionalFilter.length > 0
          ? (filters as any).regionalFilter.map((r: string) => `&regional=${r}`).join('')
          : '';
      const cellParamsA = (filters as any).cellFilter && (filters as any).cellFilter.length > 0
          ? (filters as any).cellFilter.map((c: string) => `&cell=${c}`).join('')
          : '';

      const searchParams = filters.searchFieldName && filters.searchFieldValue
          ? `&searchFieldName=${filters.searchFieldName}&searchFieldValue=${encodeURIComponent(filters.searchFieldValue)}`
          : '';

    const url = `/occurrence/analytics/${userRole}/quantity${finalizedFilter === true ? `/finished` : finalizedFilter === false ? '' : '/all'}?${filters.criticalityFilter ? `&criticality=${filters.criticalityFilter}` : ''}${typeFilter}${filters.chassisFilter ? `&chassis=${filters.chassisFilter}` : ''}${createdAtFilter}${updatedAtFilter}${dealershipParams}${regionalParamsA}${cellParamsA}${filters.stepFilter ? `&stepType=${filters.stepFilter}` : ''}${searchParams}`;
    const response = await apiService.post(url, {
      chassisList: filters.chassisListFilter,
      models: filters.modelFilter && filters.modelFilter.length > 0 ? filters.modelFilter : undefined,
    }, { signal: cancelAbortControllerAnalytics.signal });
      if (response.status === 200) {
          cancelAbortControllerAnalytics = undefined;
          return response.data as QuantityOccurrenceAnalytics;
      } else {
          cancelAbortControllerAnalytics = undefined;
          return {
              monthly: {},
              daily: {},
          };
      }
  } catch (error: any) {
      if(error.code === "ERR_CANCELED") throw new Error("ERR_CANCELED");
      cancelAbortControllerAnalytics = undefined;
      console.error(error);
      return {
          monthly: {},
          daily: {},
      };
  }
};

export const fetchOccurrenceStepAvailable = async (): Promise<OccurrenceStepEnum[]> => {
  if (cancelAbortControllerStepAvailable) cancelAbortControllerStepAvailable.abort();
  cancelAbortControllerStepAvailable = new AbortController();

  const apiService = new ApiService();
  try {
    const response = await apiService.get(`/users/permissions/occurrence`, {
      signal: cancelAbortControllerStepAvailable.signal,
    });
    if (response.status === 200) {
      cancelAbortControllerStepAvailable = undefined;
      return (
        Object.keys(response.data ?? {})
          .map((step: any) => ({ step: step, value: (response.data as any)[step] }))
          // .filter((step: any) => step.value === 'W')
          .map((step: any) => step.step)
          .sort((a: string, b: string) => {
            const steps = Object.values(OccurrenceStepEnum);
            return steps.indexOf(a as OccurrenceStepEnum) - steps.indexOf(b as OccurrenceStepEnum);
          })
      );
    } else {
      cancelAbortControllerStepAvailable = undefined;
      return [];
    }
  } catch (error: any) {
    if (error.code === 'ERR_CANCELED') throw new Error('ERR_CANCELED');
    cancelAbortControllerStepAvailable = undefined;
    console.error(error);
    return [];
  }
};

export interface IOccurrenceSummary {
  uuid: string;
  protocolNumber: string;
  vehicleChassis: string;
}

export const fetchOccurrenceSummary = async (
  profile?: BudgetRecipientEnum,
  protocolNumber?: string,
  stepFilter?: OccurrenceStepEnum | null,
  chassis?: string,
): Promise<IOccurrenceSummary[]> => {

  if (!profile) {
    return [];
  }

  const apiService = new ApiService();
  try {
    const params = new URLSearchParams();
    if (protocolNumber) params.append('protocolNumber', protocolNumber);
    if (stepFilter) params.append('stepType', stepFilter);
    if (chassis) params.append("chassis", chassis);
    params.append('page', '1');
    params.append('size', '20');

    const persona = profile === BudgetRecipientEnum.TOWER ? 'TOWER' : profile;

    const response = await apiService.get(`/occurrence/${persona}/summary?${params.toString()}`);
    if (response.status === 200 && response.data) {
      return Array.isArray(response.data) ? response.data : (response.data as any).content || [];
    }
    return [];
  } catch (error) {
    console.error('Erro ao buscar resumo de ocorrências:', error);
    return [];
  }
};
